import { createAsyncThunk } from '@reduxjs/toolkit';

import { ISubTopic } from '.';
import { AdminApiPaths } from '../../dataProvider/APIPaths';
import { IResourceFile } from '../ResourcesSlice';
import APIDataProvider from '../../dataProvider/APIDataProvider';

export interface IGetAllSubTopics {
  offset?: number;
  limit?: number;
  topicId: string;
}
export interface IAdminCreateSubTopic {
  topicId: string;
  subTopic: string;
  resources?: IResourceFile[];
  sortingOrder?: number;
}
export interface IAdminUpdateSubTopic {
  subTopicId: string;
  subTopic: string;
  resources?: IResourceFile[];
  sortingOrder?: number;
}
export interface IAdminAddSubTopicResouces {
  subTopicId: string;
  resources: IResourceFile[];
}
export interface IAdminAddSubTopicVideoLink {
  subTopicId: string;
  title: string;
  link: string;
}
export interface IAdminRemoveSubTopicVideoLink {
  subTopicId: string;
  videoLinkId: string;
}
export interface IAdminEditSubTopicVideoLink {
  subTopicId: string;
  videoLinkId: string;
  title: string;
  link: string;
}
export interface IAdminSortSubTopicVideoLink {
  subTopicId: string;
  videoLinkId: string;
  drag: 'up' | 'down';
}
export interface IAdminRemoveSubTopicResouces {
  subTopicId: string;
  resourceId: string;
}
export interface IAdminAddSubTopicContent {
  subTopicId: string;
  details: string;
}

export interface IAdminRemoveSubTopicContent {
  subTopicId: string;
  contentId: string;
}
export interface IAdminEditSubTopicContent {
  subTopicId: string;
  contentId: string;
  details: string;
}
export interface IAdminAddSubTopicFAQ {
  subTopicId: string;
  question: string;
  answer: string;
}

export interface IAdminRemoveSubTopicFAQ {
  subTopicId: string;
  FAQId: string;
}
export interface IAdminEditSubTopicFAQ {
  subTopicId: string;
  FAQId: string;
  question: string;
  answer: string;
}

export interface IAdminAddSubTopicReview {
  subTopicId: string;
  review: string;
  rating: number;
  reviewer: string;
  date: number;
}

export interface IAdminRemoveSubTopicReview {
  subTopicId: string;
  reviewId: string;
}
export interface IAdminEditSubTopicReview {
  subTopicId: string;
  reviewId: string;
  review: string;
  rating: number;
  reviewer: string;
  date: number;
}
export interface ISubTopicState {
  status: 'idle' | 'loading' | 'rejected';
  count: number;
  list: Array<ISubTopic>;
  subTopic: ISubTopic | undefined;
}

const SubTopicAdminApiDataProvider = new APIDataProvider<ISubTopic>();

const adminGetSubTopicListModule = async (data: IGetAllSubTopics) => {
  let path = `${AdminApiPaths.sub_topics}?topicId=${data.topicId}`;

  if (data.offset) {
    path += `&offset=${data.offset}`;
  }

  if (data.limit) {
    path += `&limit=${data.limit}`;
  }

  const response = await SubTopicAdminApiDataProvider.get(path);

  return response.data as {
    limit: number;
    offset: number;
    totalDocs: number;
    docs: Array<ISubTopic>;
  };
};

export const adminGetSubTopicList = createAsyncThunk(
  'admin/subtopic/list',
  adminGetSubTopicListModule
);

export const adminLoadMoreSubTopicList = createAsyncThunk(
  'admin/subtopic/loadMore',
  adminGetSubTopicListModule
);

export const adminCreateSubTopic = createAsyncThunk(
  'admin/subtopic/create',
  async (data: IAdminCreateSubTopic) => {
    const response = await SubTopicAdminApiDataProvider.create(
      AdminApiPaths.sub_topics,
      data
    );

    return response.data;
  }
);

export const adminDeleteSubTopic = createAsyncThunk(
  'admin/subtopic/delete',
  async (data: { id: string }) => {
    const response = await SubTopicAdminApiDataProvider.deleteById(
      `${AdminApiPaths.sub_topics}/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminUpdateSubTopic = createAsyncThunk(
  'admin/subtopic/update',
  async (data: IAdminUpdateSubTopic) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}`,
      data
    );

    return response.data;
  }
);

export const adminAddSubTopicResources = createAsyncThunk(
  'admin/subtopic/addResources',
  async (data: IAdminAddSubTopicResouces) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/addResources`,
      data
    );

    return response.data;
  }
);

export const adminAddSubTopicVideoLink = createAsyncThunk(
  'admin/subtopic/addVideoLink',
  async (data: IAdminAddSubTopicVideoLink) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/addVideoLink`,
      data
    );

    return response.data;
  }
);

export const adminRemoveSubTopicVideoLink = createAsyncThunk(
  'admin/subtopic/removeVideoLink',
  async (data: IAdminRemoveSubTopicVideoLink) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/removeVideoLink`,
      data
    );

    return response.data;
  }
);

export const adminEditSubTopicVideoLink = createAsyncThunk(
  'admin/subtopic/editVideoLink',
  async (data: IAdminEditSubTopicVideoLink) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/editVideoLink`,
      data
    );

    return response.data;
  }
);

export const adminRemoveSubTopicResources = createAsyncThunk(
  'admin/subtopic/removeResources',
  async (data: IAdminRemoveSubTopicResouces) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/removeResources`,
      data
    );

    return response.data;
  }
);

export const adminSortSubTopicVideoLink = createAsyncThunk(
  'admin/subtopic/sortVideoLinks',
  async (data: IAdminSortSubTopicVideoLink) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/sortVideoLinks`,
      data
    );

    return response.data;
  }
);

export const adminGetSubTopicById = createAsyncThunk(
  'admin/subtopic/id',
  async (data: { id: string }) => {
    const response = await SubTopicAdminApiDataProvider.getById(
      `${AdminApiPaths.sub_topics}/${data.id}`
    );

    return response.data;
  }
);

export const adminAddSubTopicContent = createAsyncThunk(
  'admin/subtopic/addContent',
  async (data: IAdminAddSubTopicContent) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/addContent`,
      data
    );

    return response.data;
  }
);

export const adminEditSubTopicContent = createAsyncThunk(
  'admin/subtopic/editContent',
  async (data: IAdminEditSubTopicContent) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/editContent`,
      data
    );

    return response.data;
  }
);

export const adminRemoveSubTopicContent = createAsyncThunk(
  'admin/subtopic/removeContent',
  async (data: IAdminRemoveSubTopicContent) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/removeContent`,
      data
    );

    return response.data;
  }
);

export const adminAddSubTopicFAQ = createAsyncThunk(
  'admin/subtopic/addFAQ',
  async (data: IAdminAddSubTopicFAQ) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/addFAQ`,
      data
    );
    return response.data;
  }
);

export const adminEditSubTopicFAQ = createAsyncThunk(
  'admin/subtopic/editFAQ',
  async (data: IAdminEditSubTopicFAQ) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/editFAQ`,
      data
    );
    return response.data;
  }
);

export const adminRemoveSubTopicFAQ = createAsyncThunk(
  'admin/subtopic/removeFAQ',
  async (data: IAdminRemoveSubTopicFAQ) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/removeFAQ`,
      data
    );
    return response.data;
  }
);

export const adminAddSubTopicReview = createAsyncThunk(
  'admin/subtopic/addReview',
  async (data: IAdminAddSubTopicReview) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/addReview`,
      data
    );
    return response.data;
  }
);

export const adminEditSubTopicReview = createAsyncThunk(
  'admin/subtopic/editReview',
  async (data: IAdminEditSubTopicReview) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/editReview`,
      data
    );
    return response.data;
  }
);

export const adminRemoveSubTopicReview = createAsyncThunk(
  'admin/subtopic/removeReview',
  async (data: IAdminRemoveSubTopicReview) => {
    const response = await SubTopicAdminApiDataProvider.updateById(
      `${AdminApiPaths.sub_topics}/${data.subTopicId}/removeReview`,
      data
    );
    return response.data;
  }
);
